<template>
  <KekkleBottomSheet v-model:open="isOpen" shadow="blue" padding="normal">
    <div class="dark:text-slate-200">
      <h2 class="mb-4 text-xl font-bold dark:text-slate-200">
        📅 {{ $t("pages.event.export_calendar") }}
      </h2>

      <information-text
        class="mb-4"
        :information="$t('pages.event.24_hours')"
      ></information-text>

      <p class="mb-4 dark:text-slate-200">
        {{ $t("pages.event.import_ics_text") }}
      </p>

      <a
        href="https://support.google.com/calendar/answer/37100?hl=en&ref_topic=10510447"
        target="_blank"
        class="mb-4 block text-blue-500 underline dark:text-blue-400"
      >
        {{ $t("pages.event.ice_google_text") }}
      </a>

      <p
        v-if="icalLink"
        class="relative my-4 cursor-pointer rounded-lg border-2 border-kekkle-dark-dark bg-gray-200 p-3 pr-10 wrap-break-word text-gray-500 select-text dark:bg-kekkle-dark-light dark:text-slate-200"
        @click="copyCalendarLink"
      >
        <clipboard-icon
          class="absolute top-3 right-2 h-6 dark:text-slate-200"
        ></clipboard-icon>
        {{ icalLink }}
      </p>

      <default-button
        v-if="icalLink"
        class="mt-4 w-full"
        @click="copyCalendarLink"
      >
        <p class="font-medium">📋 {{ $t("pages.photo.copy_link") }}</p>
      </default-button>
    </div>
  </KekkleBottomSheet>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { setSuccessToast } from "@/helpers/notifications/toast";
import { ClipboardIcon } from "@heroicons/vue/outline";
import { logEvent } from "firebase/analytics";
import { firebaseAnalytics } from "@/helpers/firebase/firebase";
import { copyToClipboardFlutter } from "@/helpers/flutter/features/sharing";
import { isRunningInFlutter } from "@/helpers/flutter/capabilities";
import KekkleBottomSheet from "./ui/KekkleBottomSheet.vue";
import DefaultButton from "./Buttons/DefaultButton.vue";
import InformationText from "./InformationText.vue";

const { t } = useI18n();
const groupsStore = useGroupsStore();

const isOpen = defineModel<boolean>("open", { required: true });

const icalLink = computed(() => {
  if (groupsStore.groupIcalToken) {
    return (
      import.meta.env.VITE_FUNCTIONS_URL +
      "getEventsIcs?token=" +
      groupsStore.groupIcalToken
    );
  }
  return null;
});

function copyCalendarLink() {
  if (!icalLink.value) return;

  if (isRunningInFlutter()) {
    copyToClipboardFlutter(icalLink.value, "pages.event.link_copied");
  } else {
    navigator.clipboard.writeText(icalLink.value);
    setSuccessToast({ message: t("pages.event.link_copied") });
  }

  logEvent(firebaseAnalytics, "agenda_interaction", {
    name: "copied_link",
  });

  // Close the bottom sheet after copying
  isOpen.value = false;
}
</script>
